'use client';

import { useState } from 'react';
import { toast } from 'react-toastify';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Box, Button, Dialog, TextField, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { ReviewTrigger } from '@/services/api/review.api';
import { useSubmitReview } from '@/services/query/review.queries';

interface ReviewPromptModalProps {
  open: boolean;
  /** Called only after a review has been successfully submitted. */
  onSubmitted: () => void;
  trigger?: ReviewTrigger | null;
}

/**
 * One-question experience prompt shown once, after the user's 2nd trade or 2nd
 * deposit. It is MANDATORY: there is no close button, no "not now", and the
 * backdrop / Escape key can't dismiss it. The only way out is to fill in the
 * comment and submit.
 */
export const ReviewPromptModal = ({ open, onSubmitted, trigger }: ReviewPromptModalProps) => {
  const { t } = useT();
  const [comment, setComment] = useState('');
  const { mutateAsync, isPending } = useSubmitReview();

  const trimmed = comment.trim();
  const canSubmit = trimmed.length > 0 && !isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await mutateAsync({ comment: trimmed, trigger: trigger || 'manual' });
      toast.success(t('reviews.thanks', { defaultValue: 'Thanks for sharing your feedback!' }));
      setComment('');
      onSubmitted();
    } catch {
      toast.error(
        t('reviews.error', { defaultValue: 'Could not submit your review. Please try again.' }),
      );
    }
  };

  return (
    <Dialog
      open={open}
      // Mandatory prompt — ignore backdrop clicks and Escape; do not close.
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
    >
      <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            mb: 1.5,
          }}
        >
          <FavoriteBorderIcon />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
          {t('reviews.question', { defaultValue: 'How was your overall experience?' })}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 2 }}>
          {t('reviews.subtitle', {
            defaultValue: 'Please share a few words to continue — your feedback helps us improve.',
          })}
        </Typography>

        <TextField
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('reviews.placeholder', {
            defaultValue: 'Tell us what you liked or what we can improve…',
          })}
          multiline
          minRows={3}
          maxRows={6}
          fullWidth
          autoFocus
          required
          inputProps={{ maxLength: 2000 }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2.5, py: 1.2, mt: 2.5 }}
        >
          {isPending
            ? t('reviews.submitting', { defaultValue: 'Submitting…' })
            : t('reviews.submit', { defaultValue: 'Submit review' })}
        </Button>
      </Box>
    </Dialog>
  );
};
